/**
 * Hero slideshow — minimal awwwards-style controller.
 *  - Clip-path wipe crossfade
 *  - Ken Burns on active image (CSS)
 *  - Masked reveal of content (CSS)
 *  - Awwwards-style filling dot indicators (conic-gradient + @property)
 *  - Numeric counter flip
 *  - Pointer drag + keyboard arrows + prev/next + dot click
 *  - Pauses on hover / hidden tab / reduced motion
 */
(function () {
  var REDUCED_MOTION = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function pad2(n) { return n < 10 ? '0' + n : String(n); }

  function initSlideshow(root) {
    if (!root || root.__heroInit) return;
    root.__heroInit = true;

    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-hero-slide]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-hero-dot]'));
    var prevBtn = root.querySelector('[data-hero-prev]');
    var nextBtn = root.querySelector('[data-hero-next]');
    var counterEl = root.querySelector('[data-hero-counter-current]');
    var n = slides.length;
    if (n < 1) return;

    var active = 0;
    var timer = null;
    var autoplayDelay = parseInt(root.getAttribute('data-autoplay'), 10) || 0;
    var locked = false;
    var transitionMs = 1200;

    function updateDots() {
      dots.forEach(function (el, j) {
        var wasActive = el.classList.contains('is-active');
        // Past slides keep filled; current = running; future = empty
        el.classList.toggle('is-filled', j < active);
        el.classList.remove('is-active');
        el.setAttribute('aria-selected', j === active ? 'true' : 'false');
      });
      // Force reflow so the CSS animation restarts cleanly when the class is re-added
      if (dots[active]) {
        void dots[active].offsetWidth;
        dots[active].style.setProperty('--progress-duration', (autoplayDelay || 5) + 's');
        dots[active].classList.add('is-active');
      }
    }

    function clearAllDotFills() {
      dots.forEach(function (el) { el.classList.remove('is-filled'); });
    }

    function updateCounter(index) {
      if (!counterEl) return;
      counterEl.classList.add('is-flipping');
      setTimeout(function () {
        counterEl.textContent = pad2(index + 1);
        counterEl.classList.remove('is-flipping');
      }, 260);
    }

    function setActive(i, opts) {
      opts = opts || {};
      if (locked && !opts.force) return;
      var target = ((i % n) + n) % n;
      if (target === active && !opts.force) return;

      var prevIndex = active;
      // Going back to the first slide? Clear all fills for a clean loop
      if (target < prevIndex) clearAllDotFills();

      active = target;
      locked = true;

      slides.forEach(function (el, j) {
        el.classList.toggle('is-prev', j === prevIndex && j !== active);
        el.classList.toggle('is-active', j === active);
        el.setAttribute('aria-hidden', j === active ? 'false' : 'true');
      });

      updateCounter(active);
      updateDots();

      setTimeout(function () {
        slides.forEach(function (el, j) {
          if (j !== active) el.classList.remove('is-prev');
        });
        locked = false;
      }, transitionMs);
    }

    function next() { setActive(active + 1); }
    function prev() { setActive(active - 1); }

    function startAutoplay() {
      stopAutoplay();
      if (autoplayDelay > 0 && !REDUCED_MOTION) {
        // Kick the active dot's fill animation
        updateDots();
        timer = setTimeout(function tick() {
          next();
          timer = setTimeout(tick, autoplayDelay * 1000);
        }, autoplayDelay * 1000);
      }
    }

    function stopAutoplay() {
      if (timer) { clearTimeout(timer); timer = null; }
      // CSS pauses the dot animation on :hover automatically
    }

    if (prevBtn) prevBtn.addEventListener('click', function (e) { e.stopPropagation(); prev(); startAutoplay(); });
    if (nextBtn) nextBtn.addEventListener('click', function (e) { e.stopPropagation(); next(); startAutoplay(); });

    dots.forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.stopPropagation();
        var idx = parseInt(el.getAttribute('data-index'), 10) || 0;
        if (idx < active) clearAllDotFills();
        setActive(idx);
        startAutoplay();
      });
    });

    root.addEventListener('mouseenter', stopAutoplay);
    root.addEventListener('mouseleave', startAutoplay);

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) stopAutoplay();
      else startAutoplay();
    });

    root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft') { prev(); startAutoplay(); }
      else if (e.key === 'ArrowRight') { next(); startAutoplay(); }
    });

    // Drag / swipe
    var dragging = false;
    var startX = 0;
    var startY = 0;
    var moved = false;
    var threshold = 55;

    function onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.target.closest('a, button')) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startY = e.clientY;
      root.classList.add('is-dragging');
      stopAutoplay();
    }

    function onPointerMove(e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      var dy = e.clientY - startY;
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) moved = true;
      if (Math.abs(dy) > Math.abs(dx) * 1.4) {
        dragging = false;
        root.classList.remove('is-dragging');
        startAutoplay();
      }
    }

    function onPointerUp(e) {
      if (!dragging) return;
      dragging = false;
      root.classList.remove('is-dragging');
      var dx = e.clientX - startX;
      if (Math.abs(dx) > threshold) {
        if (dx < 0) next(); else prev();
      }
      startAutoplay();
    }

    if (n > 1 && window.PointerEvent) {
      root.addEventListener('pointerdown', onPointerDown);
      root.addEventListener('pointermove', onPointerMove);
      root.addEventListener('pointerup', onPointerUp);
      root.addEventListener('pointercancel', onPointerUp);
    }

    // Init state
    slides.forEach(function (el, j) {
      el.classList.toggle('is-active', j === 0);
      el.setAttribute('aria-hidden', j === 0 ? 'false' : 'true');
    });
    if (counterEl) counterEl.textContent = pad2(1);
    updateDots();

    // Clear the one-shot entrance class once its animation finishes so
    // subsequent slide transitions use the normal transition path.
    if (slides[0] && slides[0].classList.contains('is-entering')) {
      setTimeout(function () { slides[0].classList.remove('is-entering'); }, 1800);
    }

    startAutoplay();
  }

  function boot() {
    document.querySelectorAll('[data-hero-slideshow]').forEach(initSlideshow);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var roots = e.target.querySelectorAll('[data-hero-slideshow]');
    roots.forEach(function (r) {
      r.__heroInit = false;
      initSlideshow(r);
    });
  });
})();
